<template>
  <div class="page" id="MonthlyReportPage">
    <div class="page-title">
      <span>{{ $t('tax_report') }}</span>
      <div class="choose-currency">
        <select v-if="$route.name !== 'global'" name="currency" v-model="currency" @change="selectCurrency">
          <option v-if="getStoreConfig() && !isNotUseStoreCurrency(getStoreConfig().currency)" :value="null">{{
            getStoreConfig().currency }}</option>
          <option v-for="rate in (getCurrencyRates)" :value="rate.toCurrency" :key="rate.id">
            {{ rate.toCurrency }}
          </option>
        </select>
      </div>
    </div>
    <UiFilterbar @onDefaultFilter="onDefaultFilter" @onFilterDay="onDefaultFilter" @onChangeOutlet="onChangeOutlet"
      @onChangeOrderStatus="onChangeOrderStatus" :disableCompareDate="true" :queryMode="this.query.mode" />
    <UiReportList :reportList="reportList" />
    <UiTableBasic :columns="columns" :data="report.items" :footer="footerData">
    </UiTableBasic>
    <div class="pagination" v-if="countItems / query.limit > 1">
      <UiPagination :page="query.page - 1" :total="countItems" :limit="query.limit" @getDataTable="getDataTable" />
    </div>
  </div>
</template>

<script>
import UiFilterbar from '@/components/filterBar/UiFilterbar.vue'
import UiReportList from '@/components/UiReportList.vue'
import UiTableBasic from '@/components/UiTableBasic.vue'
import reportType from '@/config/reportType'
import localStorageKey from '@/constants/localStorageKey'
import reportList from '@/constants/reportList'
import i18n from '@/i18n'
import { getItemByLocalStorage, setLocalStorage } from '@/utils/localStorage'
import formatNum from 'format-num'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'MonthlyReportPage',
  components: {
    UiFilterbar,
    UiReportList,
    UiTableBasic
  },
  data() {
    const timeZone = Number.isInteger(Number(new Date().getTimezoneOffset()) / 60) ? (Number(new Date().getTimezoneOffset()) < 0 ? `${`${Number(new Date().getTimezoneOffset().toString().replace('-', '')) / 60}`.padStart(2, 0)}:00` : `-${Number(new Date().getTimezoneOffset()) / 60}:00`) : (Number(new Date().getTimezoneOffset()) < 0 ? `${`${Math.trunc(Number(new Date().getTimezoneOffset().toString().replace('-', '')) / 60)}`.padStart(2, 0)}:30` : `-${Math.trunc(Number(new Date().getTimezoneOffset()) / 60)}:30`)

    return {
      currency: null,
      day: {},
      query: {
        page: 1,
        limit: 20,
        mode: 'DATETIME',
        outletIds: null,
        timeZone,
        reportingMode: 'in_store'
      },
      filterType: 'DATETIME',
      reportList: reportList.tax,
      type: reportType.dailyTaxReport,
      columns: [],
      footerData: []
    }
  },

  computed: {
    ...mapGetters({
      report: 'report/getMonthlyReportData',
      getCurrencyRates: 'currency/getCurrencyRates',
      getCurrencies: 'currency/getCurrencies'
    })
  },

  watch: {
    report: {
      handler: function (newValue, oldValue) {
        const storeConfig = getItemByLocalStorage(localStorageKey.STORE_CONFIG)
        const currentCurrent = getItemByLocalStorage(localStorageKey.CURRENT_CURRENCY)
        const toCurrency = currentCurrent?.toCurrency
        const formatTotal = (total, item) => {
          const rate = item && item.presentmentCurrency && item.currencyExchangeRate && (toCurrency === item.presentmentCurrency) ? item.currencyExchangeRate : (currentCurrent?.rate || '1')
          return `${currentCurrent?.symbol || storeConfig.symbol} ${formatNum((Number(total) * Number(rate)).toFixed(2), { maxFraction: 2 })}`
        }
        const summary = newValue.summary
        this.columns = [
          {
            key: 'productCategory',
            label: i18n.t('product_tax_class'),
            footerClass: 'text-center',
            formatter: (value, row) => {
              if (!row.taxRate) return value
              return `${value} (${formatNum((Number(row.taxRate)).toFixed(2))}%)`
            }
          },
          {
            key: 'subtotalPrice',
            label: i18n.t('total_tax_excluded'),
            cellClass: 'text-right',
            formatter: (value, row) => formatTotal(value, row)
          },
          {
            key: 'totalDiscount',
            label: i18n.t('discount_total'),
            cellClass: 'text-right',
            formatter: (value, row) => `${Number(value) > 0 ? '-' : ''}${formatTotal(value, row)}`
          },
          {
            key: 'tax',
            label: i18n.t('tax'),
            cellClass: 'text-right',
            formatter: (value, row) => formatTotal(value, row)
          },
          {
            key: 'totalPrice',
            label: i18n.t('total_tax_exempt'),
            cellClass: 'text-right',
            formatter: (value, row) => formatTotal(value, row)
          },
          {
            key: 'totalPriceInReport',
            label: i18n.t('total'),
            cellClass: 'text-right',
            formatter: (value, row) => formatTotal(value, row)

          },
          {
            key: 'totalRefunded',
            label: i18n.t('refund_includes_tax'),
            cellClass: 'text-right',
            formatter: (value, row) => formatTotal(value, row)
          }
        ]
        this.countItems = newValue.countItems
        this.footerData = [{
          productCategory: i18n.t('grand_total'),
          subtotalPrice: summary.subtotalPrice,
          totalDiscount: summary.totalDiscount,
          tax: summary.tax,
          totalPrice: summary.totalPrice,
          totalRefunded: summary.totalRefunded,
          totalPriceInReport: summary.totalPriceInReport
        }]
      },
      deep: true,
      immediate: true
    }
  },

  mounted() {
    if (getItemByLocalStorage(localStorageKey.CURRENT_CURRENCY)) {
      this.currency = getItemByLocalStorage(localStorageKey.CURRENT_CURRENCY).toCurrency
    } else {
      if (this.getCurrencyRates && this.getCurrencyRates.length > 0) {
        const currencyRate = this.getCurrencyRates[0]
        if (currencyRate) {
          const storeConfig = getItemByLocalStorage(localStorageKey.STORE_CONFIG)
          const currencySelected = this.getCurrencies?.find(currencyItem => storeConfig?.currency === currencyItem.code)
          if (currencySelected) {
            const symbol = currencySelected.symbol
            setLocalStorage(localStorageKey.CURRENT_CURRENCY, {
              fromCurrency: storeConfig?.currency,
              toCurrency: storeConfig?.currency,
              rate: 1,
              symbol
            })
          }
        }
      }
    }
  },

  methods: {
    ...mapActions({
      getReport: 'report/getReportDataByStore'
    }),
    selectCurrency() {
      const currencyRate = this.getCurrencyRates.find(rate => rate.toCurrency === this.currency)

      if (currencyRate) {
        const currencySelected = this.getCurrencies.find(currencyItem => this.currency === currencyItem.code)
        const symbol = currencySelected.currency_symbol || this.currency
        var isBaseCurrency = this.getStoreConfig().currency && this.isNotUseStoreCurrency(this.getStoreConfig().currency) && this.currency === this.getStoreConfig().currency
        setLocalStorage(localStorageKey.CURRENT_CURRENCY, { ...currencyRate, symbol, rate: isBaseCurrency ? 1 : currencyRate.rate })
      }

      if (!this.currency && getItemByLocalStorage(localStorageKey.CURRENT_CURRENCY)) {
        const storeConfig = getItemByLocalStorage(localStorageKey.STORE_CONFIG)
        const currencySelected = this.getCurrencies.find(currencyItem => storeConfig?.currency === currencyItem.code)
        const symbol = currencySelected.symbol
        setLocalStorage(localStorageKey.CURRENT_CURRENCY, {
          fromCurrency: storeConfig?.currency,
          toCurrency: storeConfig?.currency,
          rate: 1,
          symbol
        })
      }

      this.getReport({
        type: reportType.monthlyReport,
        query: {
          ...this.query,
          ...this.day
        }
      })
    },
    getStoreConfig() {
      return getItemByLocalStorage(localStorageKey.STORE_CONFIG)
    },
    isNotUseStoreCurrency(currency) {
      if (currency && currency.length && this.getCurrencyRates && this.getCurrencyRates.length > 0) {
        const existCurrency = this.getCurrencyRates.find(currencyItem => currency === currencyItem.toCurrency)
        return !!existCurrency
      }
      return false
    },
    onChangeOutlet(reportingMode, outletIds, registerIds, day, type, states) {
      this.day = day
      this.query.outletIds = outletIds
      this.query.registerIds = registerIds
      this.query.reportingMode = reportingMode
      this.filterType = type
      this.query.mode = type
      this.getReport({
        type: reportType.monthlyReport,
        query: {
          ...this.query,
          ...day,
          states
        }
      })
    },
    onDefaultFilter(day, type, reportingMode, outletIds, registerIds, states) {
      this.day = day
      if (outletIds) {
        this.query.outletIds = outletIds
        this.query.registerIds = registerIds
      }
      if (reportingMode) {
        this.query.reportingMode = reportingMode
        if (reportingMode === 'online') {
          this.query.outletIds = null
        }
      }
      this.filterType = type
      this.query.mode = type
      this.getReport({
        type: reportType.monthlyReport,
        query: {
          ...this.query,
          ...day,
          states
        }
      })
    },
    onChangeOrderStatus(reportingMode, outletIds, registerIds, day, type, states) {
      this.query.outletIds = outletIds
      this.query.registerIds = registerIds
      this.query.reportingMode = reportingMode
      this.filterType = type
      this.query.mode = type
      this.getReport({
        type: reportType.monthlyReport,
        query: {
          ...this.query,
          ...day,
          states
        }
      })
      this.query.showAllOrders = reportingMode !== 'in_store'
      this.getReport({
        type: reportType.monthlyReport,
        query: {
          ...this.query,
          ...day,
          states,
          mode: null,
          timeZone: this.getTimeZone()
        }
      })
    },
    getDataTable(currentPage) {
      this.query.page = currentPage
      this.getReport({
        type: reportType.monthlyReport,
        query: {
          ...this.getQuery,
          ...this.day,
          page: currentPage
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.page {
  padding: 0 30px;
}

.page-title {
  font-size: 36px;
  line-height: 49px;
  margin-bottom: 25px;
  display: flex;
  justify-content: space-between;

  select {
    padding: 0 15px;
    margin: 0;
    height: 39px;
    min-width: 140px;
    outline: none;
    border-radius: 4px;
    border: 1px solid #818181;
    background-color: #fff;
  }
}
</style>
